import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import ActionAttachmentsZone from "@/components/ActionAttachmentsZone";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTasks, useCreateTask, useUpdateTask, useDeleteTask, useTaskComments, useCreateTaskComment, useDeleteTaskComment, useTaskPieces, useCreateTaskPiece, useDeleteTaskPiece, useArchiveTask, useUnarchiveTask, useTaskEcheanceChanges } from "@/hooks/use-tasks";
import { useProfiles } from "@/hooks/use-profiles";
import { MentionTextarea, renderWithMentions } from "@/components/mention-textarea";
import { useClients } from "@/hooks/use-clients";
import { useResellers } from "@/hooks/use-inventory";
import { useProspects } from "@/hooks/use-prospects";
import { useProducts } from "@/hooks/use-inventory";
import { useAuth } from "@/hooks/use-auth";
import { getRoleLevel, ROLE_LEVEL } from "@shared/roles";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SearchSelect } from "@/components/search-select";
import { cn, extractLocation } from "@/lib/utils";
import { handlePlainTextCopy } from "@/lib/plainTextCopy";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip, TooltipContent, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import {
  Plus, Search, Trash2, ClipboardList, LayoutGrid, List, GripVertical,
  Calendar, Send, Paperclip, MoreHorizontal, Pencil, Eye, X, ChevronDown, ChevronsUpDown, Check,
  CircleDot, ArrowUp, ArrowRight, ArrowDown, User, MessageSquare, Package, Rocket,
  Archive, ArchiveRestore, Bell, UserCheck, ArrowRightLeft, Flag, CalendarClock, CalendarPlus,
} from "lucide-react";
import { useLocation } from "wouter";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import type { Task, Profile, TaskComment, Client, Reseller, Prospect, Product, TaskPiece } from "@shared/schema";
import { profileIdSchema, type ProfileId } from "@shared/identity";
import { ContactSelector } from "@/components/ContactSelector";
import { authFetch } from "@/lib/authFetch";

const DEFAULT_LABELS = [
  { name: "R&D", color: "bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-900/30 dark:text-violet-400" },
  { name: "Technique", color: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400" },
  { name: "SAV", color: "bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400" },
  { name: "Logistique", color: "bg-teal-100 text-teal-800 border-teal-200 dark:bg-teal-900/30 dark:text-teal-400" },
  { name: "Formation", color: "bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400" },
  { name: "Cahier des charges", color: "bg-pink-100 text-pink-800 border-pink-200 dark:bg-pink-900/30 dark:text-pink-400" },
  { name: "Marketing", color: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200 dark:bg-fuchsia-900/30 dark:text-fuchsia-400" },
  { name: "Commercial", color: "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400" },
  { name: "Admin", color: "bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-900/30 dark:text-gray-400" },
  { name: "Urgent", color: "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400" },
];

const PRIORITY_CONFIG: Record<string, { label: string; icon: typeof ArrowDown; color: string }> = {
  "1": { label: "Faible", icon: ArrowDown, color: "text-emerald-600" },
  "2": { label: "Moyen", icon: ArrowRight, color: "text-amber-600" },
  "3": { label: "Fort", icon: ArrowUp, color: "text-red-600" },
};

const STATUS_LABELS: Record<string, string> = {
  a_faire: "À faire",
  en_cours: "En cours",
  fait: "Fait",
};

const STATUS_COLUMNS = ["a_faire", "en_cours", "fait"] as const;

const LABEL_TO_MISSION_TYPE: Record<string, string> = {
  "SAV": "installation",
  "Technique": "installation",
  "Formation": "formation",
  "Logistique": "livraison",
  "Commercial": "demonstration",
  "Cahier des charges": "atelier",
};

function getLabelColor(name: string): string {
  const found = DEFAULT_LABELS.find(l => l.name === name);
  return found?.color || "bg-secondary text-secondary-foreground border-border";
}

function PriorityIcon({ priority }: { priority: string }) {
  const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG["2"];
  const Icon = config.icon;
  return <Icon className={`h-4 w-4 ${config.color}`} />;
}

function LabelBadge({ name }: { name: string }) {
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${getLabelColor(name)}`} data-testid={`label-${name}`}>
      {name}
    </span>
  );
}

type TaskFormData = {
  titre: string;
  emetteurLabel: string;
  emetteurId: string | null;
  emetteurType: string;
  assigneeId: ProfileId | null;
  assigneeType: string;
  labels: string[];
  priorite: string;
  dateEcheance: string;
  notes: string;
  rappelDate: string;
  rappelNote: string;
  taskType: string;
  raisonChangementEcheance: string;
};

function taskEmitterType(value: string): "client" | "prospect" | "revendeur" | "libre" | "equipe" {
  if (value === "client" || value === "prospect" || value === "revendeur" || value === "equipe") {
    return value;
  }
  return "libre";
}

function taskPriority(value: string): "1" | "2" | "3" {
  return value === "1" || value === "2" ? value : "3";
}

function taskKind(value: string): "operationnelle" | "personnelle" {
  return value === "personnelle" ? value : "operationnelle";
}

const emptyForm: TaskFormData = {
  titre: "",
  emetteurLabel: "",
  emetteurId: null,
  emetteurType: "libre",
  assigneeId: null,
  assigneeType: "autre",
  labels: [],
  priorite: "2",
  dateEcheance: "",
  notes: "",
  rappelDate: "",
  rappelNote: "",
  taskType: "operationnelle",
  raisonChangementEcheance: "",
};


export default function TaskList() {
  const { profile } = useAuth();
  const { data: tasks, isLoading } = useTasks();
  const { data: echeanceChanges = {} } = useTaskEcheanceChanges();
  const { data: profiles } = useProfiles();
  const { data: clientsList } = useClients();
  const { data: resellersList } = useResellers();
  const { data: prospectsList } = useProspects();
  const createMutation = useCreateTask();
  const updateMutation = useUpdateTask();
  const deleteMutation = useDeleteTask();
  const archiveTask = useArchiveTask();
  const unarchiveTask = useUnarchiveTask();
  const { toast } = useToast();
  const parseProfileId = (value: string | null): ProfileId | null => {
    const parsed = value ? profileIdSchema.safeParse(value) : null;
    return parsed?.success ? parsed.data : null;
  };

  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<"kanban" | "list">("kanban");
  const [showArchived, setShowArchived] = useState(false);
  const [archiveConfirmId, setArchiveConfirmId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [draftTaskId, setDraftTaskId] = useState<string | null>(null);
  const [detailTask, setDetailTask] = useState<Task | null>(null);
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (deepLinkHandled.current || !tasks?.length) return;
    const params = new URLSearchParams(window.location.search);
    const openTaskId = params.get("taskId");
    if (!openTaskId) return;
    deepLinkHandled.current = true;
    const found = (tasks as Task[]).find((t) => t.id === openTaskId);
    if (found) setDetailTask(found);
  }, [tasks]);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterLabel, setFilterLabel] = useState<string>("all");
  const [filterPriority, setFilterPriority] = useState<string>("all");
  const [filterAssignee, setFilterAssignee] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"priority" | "echeance" | "creation">("priority");
  const [formData, setFormData] = useState<TaskFormData>({ ...emptyForm });
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const [newLabelInput, setNewLabelInput] = useState("");

  const allLabels = useMemo(() => {
    const set = new Set(DEFAULT_LABELS.map(l => l.name));
    (tasks || []).forEach((t: Task) => {
      (t.labels || []).forEach(l => set.add(l));
    });
    return Array.from(set);
  }, [tasks]);

  const activeProfiles = useMemo(() =>
    (profiles || []).filter((p: Profile) => p.isActive),
    [profiles]
  );

  const viewerLevel = getRoleLevel((profile as any)?.accessRole);
  const assignableProfiles = useMemo(() => {
    if (viewerLevel <= ROLE_LEVEL.technicien) return [];
    return activeProfiles.filter((p: Profile) =>
      getRoleLevel((p as any).accessRole) < viewerLevel
    );
  }, [activeProfiles, viewerLevel]);

  const emetteurOptions = useMemo(() => {
    const opts: { value: string; label: string; sublabel?: string; type: string }[] = [];
    (clientsList || []).forEach((c: Client) => {
      opts.push({
        value: `client::${c.id}`,
        label: (c as any).nomCommercial || c.companyName,
        sublabel: extractLocation(c.address),
        type: "client",
      });
    });
    (resellersList || []).forEach((r: Reseller) => {
      opts.push({
        value: `revendeur::${r.id}`,
        label: (r as any).nomCommercial || r.companyName,
        sublabel: extractLocation(r.address),
        type: "revendeur",
      });
    });
    (prospectsList || []).filter((p: Prospect) => p.actif).forEach((p: Prospect) => {
      const loc = [p.codePostal, p.ville].filter(Boolean).join(" ");
      opts.push({
        value: `prospect::${p.id}`,
        label: p.societe,
        sublabel: loc || undefined,
        type: "prospect",
      });
    });
    const roleLabels: Record<string, string> = {
      technicien: "Technicien",
      commercial: "Commercial",
      administration: "Administration",
    };
    (profiles || []).filter((p: Profile) => p.isActive && ["technicien", "commercial", "administration"].includes(p.role)).forEach((p: Profile) => {
      opts.push({
        value: `equipe::${p.id}`,
        label: `${p.firstName} ${p.lastName}`.trim(),
        sublabel: roleLabels[p.role] || p.role,
        type: "equipe",
      });
    });
    return opts;
  }, [clientsList, resellersList, prospectsList, profiles]);

  const getProfileName = (id: string | null) => {
    if (!id) return "—";
    const p = activeProfiles.find((pr: Profile) => pr.id === id);
    return p ? `${p.firstName} ${p.lastName}`.trim() : "—";
  };

  const getProfileRole = (id: string | null) => {
    if (!id) return null;
    const p = activeProfiles.find((pr: Profile) => pr.id === id);
    return p?.role || null;
  };

  const handleArchiveTask = async (taskId: string) => {
    try {
      await archiveTask.mutateAsync(taskId);
      toast({ title: "Action archivée" });
      setArchiveConfirmId(null);
    } catch {
      toast({ title: "Erreur", variant: "destructive" });
    }
  };

  const handleUnarchiveTask = async (taskId: string) => {
    try {
      await unarchiveTask.mutateAsync(taskId);
      toast({ title: "Action désarchivée" });
    } catch {
      toast({ title: "Erreur", variant: "destructive" });
    }
  };

  const filteredTasks = useMemo(() => {
    const filtered = (tasks || []).filter((t: Task) => {
      const isArchived = (t as any).archive === true;
      if (showArchived && !isArchived) return false;
      if (!showArchived && isArchived) return false;
      if (filterStatus !== "all" && t.status !== filterStatus) return false;
      if (filterLabel !== "all" && !(t.labels || []).includes(filterLabel)) return false;
      if (filterPriority !== "all" && t.priorite !== filterPriority) return false;
      if (filterAssignee !== "all" && t.assigneeId !== filterAssignee) return false;
      if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        return t.titre.toLowerCase().includes(lower) ||
          (t.emetteurLabel || t.emetteur || "").toLowerCase().includes(lower) ||
          (t.notes || "").toLowerCase().includes(lower);
      }
      return true;
    });
    return filtered.sort((a: Task, b: Task) => {
      if (sortBy === "priority") {
        const pa = parseInt(a.priorite) || 2;
        const pb = parseInt(b.priorite) || 2;
        if (pa !== pb) return pb - pa;
        return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
      }
      if (sortBy === "echeance") {
        const da = a.dateEcheance || "9999-12-31";
        const db = b.dateEcheance || "9999-12-31";
        if (da !== db) return da.localeCompare(db);
        return parseInt(b.priorite || "2") - parseInt(a.priorite || "2");
      }
      return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
    });
  }, [tasks, filterStatus, filterLabel, filterPriority, filterAssignee, searchTerm, sortBy, showArchived]);

  const resetForm = () => setFormData({ ...emptyForm });

  const openCreate = useCallback(async () => {
    resetForm();
    try {
      const res = await authFetch('/api/tasks/draft', { method: 'POST' });
      if (res.ok) {
        const { id } = await res.json();
        setDraftTaskId(id);
      }
    } catch {}
    setCreateOpen(true);
  }, []);

  const handleCancelCreate = useCallback(async () => {
    if (draftTaskId) {
      authFetch(`/api/tasks/${draftTaskId}/draft-cancel`, { method: 'DELETE' }).catch(() => {});
      setDraftTaskId(null);
    }
    setCreateOpen(false);
    resetForm();
  }, [draftTaskId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.titre.trim()) return;
    try {
      const payload = {
        titre: formData.titre.trim(),
        emetteur: formData.emetteurLabel.trim() || null,
        emetteurLabel: formData.emetteurLabel.trim() || null,
        emetteurId: formData.emetteurId || null,
        emetteurType: formData.emetteurId || formData.emetteurLabel.trim()
          ? taskEmitterType(formData.emetteurType)
          : null,
        assigneeId: profile?.accessRole === 'technicien' ? (profile?.id || null) : (formData.assigneeId || null),
        assigneeType: (profile as any)?.accessRole === 'technicien' ? "technicien" : formData.assigneeId ? (getProfileRole(formData.assigneeId) === "technicien" ? "technicien" : getProfileRole(formData.assigneeId) === "commercial" ? "commercial" : "autre") : "autre",
        labels: formData.labels.length > 0 ? formData.labels : null,
        priorite: taskPriority(formData.priorite),
        status: "a_faire" as const,
        dateEcheance: formData.dateEcheance || null,
        notes: formData.notes || null,
        missionId: null,
        createdBy: profile?.id || null,
        rappelDate: formData.rappelDate ? new Date(formData.rappelDate) : null,
        rappelNote: formData.rappelNote || null,
        taskType: taskKind(formData.taskType),
      };
      if (draftTaskId) {
        const res = await authFetch(`/api/tasks/${draftTaskId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, isDraft: false }),
        });
        if (!res.ok) throw new Error("Impossible de publier le brouillon");
        queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
        setDraftTaskId(null);
      } else {
        await createMutation.mutateAsync(payload);
      }
      setCreateOpen(false);
      resetForm();
      toast({ title: "Action créée" });
    } catch {
      toast({ variant: "destructive", title: "Erreur", description: "Impossible de créer l'action" });
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTask || !formData.titre.trim()) return;
    const isClearingDeadline = !formData.dateEcheance && !!(editingTask.dateEcheance);
    const isHighPriority = formData.priorite === "3";
    if (isClearingDeadline && isHighPriority && !formData.raisonChangementEcheance?.trim()) {
      toast({ variant: "destructive", title: "Raison requise", description: "Veuillez indiquer une raison pour supprimer l'échéance d'une tâche prioritaire." });
      return;
    }
    try {
      await updateMutation.mutateAsync({
        id: editingTask.id,
        titre: formData.titre.trim(),
        emetteur: formData.emetteurLabel.trim() || null,
        emetteurLabel: formData.emetteurLabel.trim() || null,
        emetteurId: formData.emetteurId || null,
        emetteurType: formData.emetteurId || formData.emetteurLabel.trim()
          ? taskEmitterType(formData.emetteurType)
          : null,
        assigneeId: profile?.accessRole === 'technicien' ? (profile?.id || null) : (formData.assigneeId || null),
        assigneeType: (profile as any)?.accessRole === 'technicien' ? "technicien" : formData.assigneeId ? (getProfileRole(formData.assigneeId) === "technicien" ? "technicien" : getProfileRole(formData.assigneeId) === "commercial" ? "commercial" : "autre") : "autre",
        labels: formData.labels.length > 0 ? formData.labels : null,
        priorite: taskPriority(formData.priorite),
        dateEcheance: formData.dateEcheance || null,
        notes: formData.notes || null,
        rappelDate: formData.rappelDate ? new Date(formData.rappelDate) : null,
        rappelNote: formData.rappelNote || null,
        taskType: taskKind(formData.taskType),
      });
      setEditingTask(null);
      resetForm();
      toast({ title: "Action modifiée" });
    } catch {
      toast({ variant: "destructive", title: "Erreur", description: "Impossible de modifier l'action" });
    }
  };

  const handleStatusChange = async (taskId: string, newStatus: string) => {
    try {
      await updateMutation.mutateAsync({ id: taskId, status: newStatus as any });
      if (newStatus === "fait") {
        setArchiveConfirmId(taskId);
      }
    } catch {
      toast({ variant: "destructive", title: "Erreur", description: "Impossible de mettre à jour le statut" });
    }
  };

  const handleQuickAssign = async (taskId: string, profileId: string) => {
    const validatedProfileId = parseProfileId(profileId);
    if (!validatedProfileId) return;
    const role = getProfileRole(profileId);
    const assigneeType = role === "technicien" ? "technicien" : role === "commercial" ? "commercial" : "autre";
    try {
      await updateMutation.mutateAsync({ id: taskId, assigneeId: validatedProfileId, assigneeType });
      toast({ title: "Action assignée" });
    } catch {
      toast({ variant: "destructive", title: "Erreur", description: "Impossible d'assigner l'action" });
    }
  };

  const handleDelete = async (taskId: string) => {
    if (!confirm("Supprimer cette action ?")) return;
    try {
      await deleteMutation.mutateAsync(taskId);
      setDetailTask(null);
      toast({ title: "Action supprimée" });
    } catch {
      toast({ variant: "destructive", title: "Erreur", description: "Impossible de supprimer l'action" });
    }
  };

  const openEdit = (task: Task) => {
    setFormData({
      titre: task.titre,
      emetteurLabel: task.emetteurLabel || task.emetteur || "",
      emetteurId: task.emetteurId || null,
      emetteurType: task.emetteurType || "libre",
      assigneeId: task.assigneeId || null,
      assigneeType: task.assigneeType || "autre",
      labels: task.labels || [],
      priorite: task.priorite,
      dateEcheance: task.dateEcheance || "",
      notes: task.notes || "",
      rappelDate: task.rappelDate ? task.rappelDate.slice(0, 16) : "",
      rappelNote: (task as any).rappelNote || "",
      taskType: (task as any).taskType || "operationnelle",
    });
    setEditingTask(task);
  };

  const toggleLabel = (label: string) => {
    setFormData(prev => ({
      ...prev,
      labels: prev.labels.includes(label)
        ? prev.labels.filter(l => l !== label)
        : [...prev.labels, label],
    }));
  };

  const handleDragStart = (e: React.DragEvent, taskId: string) => {
    setDraggedTaskId(taskId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e: React.DragEvent, newStatus: string) => {
    e.preventDefault();
    if (draggedTaskId) {
      handleStatusChange(draggedTaskId, newStatus);
      setDraggedTaskId(null);
    }
  };

  const renderTaskForm = (onSubmit: (e: React.FormEvent) => void, submitLabel: string, isPending: boolean, actionId?: string, onCancel?: () => void) => (
    <form onSubmit={onSubmit} className="flex flex-col flex-1 overflow-hidden min-h-0">
    <div className="flex-1 overflow-y-auto pr-1 space-y-4 pt-2">
      <div className="space-y-2">
        <Label>Titre *</Label>
        <Input
          required
          value={formData.titre}
          onChange={e => setFormData(prev => ({ ...prev, titre: e.target.value }))}
          placeholder="Titre de l'action"
          data-testid="input-task-titre"
        />
      </div>
      <div className="space-y-2">
        <Label>Type de tâche</Label>
        <div className="flex gap-2">
          {[
            { value: "operationnelle", label: "Opérationnelle" },
            { value: "personnelle", label: "Personnelle" },
          ].map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, taskType: opt.value }))}
              className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${formData.taskType === opt.value ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}
              data-testid={`btn-task-type-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">Une tâche personnelle n'est visible que par vous.</p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Émetteur</Label>
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => {
                const isLibre = !formData.emetteurId && (formData.emetteurType === "libre" || !formData.emetteurType);
                if (isLibre) {
                  setFormData(prev => ({ ...prev, emetteurId: null, emetteurType: "search", emetteurLabel: "" }));
                } else {
                  setFormData(prev => ({ ...prev, emetteurId: null, emetteurType: "libre", emetteurLabel: "" }));
                }
              }}
              data-testid="toggle-emetteur-mode"
            >
              {(!formData.emetteurId && (formData.emetteurType === "libre" || !formData.emetteurType)) ? "Rechercher un contact" : "Saisie libre"}
            </button>
          </div>
          <EmetteurSelect
            value={formData.emetteurId ? `${formData.emetteurType}::${formData.emetteurId}` : null}
            freeText={formData.emetteurType === "libre" ? formData.emetteurLabel : ""}
            forceMode={(!formData.emetteurId && formData.emetteurType === "libre") ? "libre" : "search"}
            options={emetteurOptions}
            onSelect={(emId, emType, emLabel) => {
              setFormData(prev => ({ ...prev, emetteurId: emId, emetteurType: emType, emetteurLabel: emLabel }));
            }}
            data-testid="input-task-emetteur"
          />
        </div>
        <div className="space-y-2">
          <Label>Assigné à</Label>
          {(profile as any)?.accessRole === 'technicien' ? (
            <div className="h-9 px-3 flex items-center border rounded-md bg-muted/50 text-sm text-muted-foreground">
              {profile ? `${profile.firstName} ${profile.lastName}`.trim() : "Moi"} (auto)
            </div>
          ) : (
            <SearchSelect
              value={formData.assigneeId}
              onValueChange={v => setFormData(prev => ({ ...prev, assigneeId: parseProfileId(v) }))}
              options={assignableProfiles.map((p: Profile) => ({
                value: p.id,
                label: `${p.firstName} ${p.lastName}`.trim(),
                sublabel: p.role === "administration" ? "Admin" : p.role === "technicien" ? "Technicien" : p.role === "commercial" ? "Commercial" : p.role || undefined,
              }))}
              placeholder="Sélectionner..."
              emptyText="Aucun profil"
              data-testid="select-task-assignee"
            />
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Priorité</Label>
          <Select value={formData.priorite} onValueChange={v => setFormData(prev => ({ ...prev, priorite: v }))}>
            <SelectTrigger data-testid="select-task-priorite"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">🟢 Faible</SelectItem>
              <SelectItem value="2">🟠 Moyen</SelectItem>
              <SelectItem value="3">🔴 Fort</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Échéance</Label>
          <Input
            type="date"
            value={formData.dateEcheance}
            onChange={e => setFormData(prev => ({ ...prev, dateEcheance: e.target.value, raisonChangementEcheance: "" }))}
            data-testid="input-task-echeance"
          />
        </div>
      </div>
      {editingTask && (
        (formData.dateEcheance !== (editingTask.dateEcheance || "") && !!formData.dateEcheance) ||
        (!formData.dateEcheance && !!(editingTask.dateEcheance))
      ) && (() => {
        const isClearingDeadline = !formData.dateEcheance && !!(editingTask.dateEcheance);
        const isHighPriority = formData.priorite === "3";
        const raisonRequise = isClearingDeadline && isHighPriority;
        return (
          <div className="space-y-2" data-testid="section-raison-echeance">
            <Label>
              Raison du changement{" "}
              {raisonRequise
                ? <span className="text-destructive font-normal">(requis)</span>
                : <span className="text-muted-foreground font-normal">(optionnel)</span>
              }
            </Label>
            <Textarea
              value={formData.raisonChangementEcheance}
              onChange={e => setFormData(prev => ({ ...prev, raisonChangementEcheance: e.target.value }))}
              placeholder="Pourquoi cette échéance change-t-elle ?"
              className={`h-16 resize-none text-sm${raisonRequise && !formData.raisonChangementEcheance?.trim() ? " border-destructive" : ""}`}
              data-testid="input-raison-changement-echeance"
              required={raisonRequise}
            />
            {raisonRequise && !formData.raisonChangementEcheance?.trim() && (
              <p className="text-xs text-destructive" data-testid="error-raison-requise">
                Une raison est obligatoire pour supprimer l'échéance d'une tâche prioritaire.
              </p>
            )}
          </div>
        );
      })()}
      <div className="space-y-2">
        <Label>Labels</Label>
        <div className="flex flex-wrap gap-1.5">
          {allLabels.map(label => (
            <button
              key={label}
              type="button"
              onClick={() => toggleLabel(label)}
              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border cursor-pointer transition-all ${
                formData.labels.includes(label)
                  ? getLabelColor(label) + " ring-2 ring-primary/30"
                  : "bg-muted/40 text-muted-foreground border-border opacity-60 hover:opacity-100"
              }`}
              data-testid={`toggle-label-${label}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-center mt-1">
          <Input
            value={newLabelInput}
            onChange={e => setNewLabelInput(e.target.value)}
            placeholder="Nouveau label..."
            className="h-7 text-xs flex-1"
            data-testid="input-new-label"
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                const name = newLabelInput.trim();
                if (name && !formData.labels.includes(name)) {
                  setFormData(prev => ({ ...prev, labels: [...prev.labels, name] }));
                  setNewLabelInput("");
                }
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => {
              const name = newLabelInput.trim();
              if (name && !formData.labels.includes(name)) {
                setFormData(prev => ({ ...prev, labels: [...prev.labels, name] }));
                setNewLabelInput("");
              }
            }}
            data-testid="btn-add-label"
          >
            <Plus className="h-3 w-3 mr-1" />Ajouter
          </Button>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea
          value={formData.notes}
          onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
          rows={4}
          placeholder="Contexte, mails, cahier des charges..."
          data-testid="input-task-notes"
        />
      </div>
      {actionId && (
        <ActionAttachmentsZone actionId={actionId} />
      )}
      <div className="border rounded-lg p-3 space-y-3 bg-amber-50/50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800/40">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-amber-600" />
          <Label className="text-sm font-medium text-amber-800 dark:text-amber-400">Rappel (optionnel)</Label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Date et heure</Label>
            <Input
              type="datetime-local"
              value={formData.rappelDate}
              onChange={e => setFormData(prev => ({ ...prev, rappelDate: e.target.value }))}
              className="h-8 text-sm"
              data-testid="input-task-rappel-date"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Note de rappel</Label>
            <Input
              value={formData.rappelNote}
              onChange={e => setFormData(prev => ({ ...prev, rappelNote: e.target.value }))}
              placeholder="Que faire à ce moment-là..."
              className="h-8 text-sm"
              data-testid="input-task-rappel-note"
            />
          </div>
        </div>
        {formData.rappelDate && (
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-destructive transition-colors"
            onClick={() => setFormData(prev => ({ ...prev, rappelDate: "", rappelNote: "" }))}
          >
            ✕ Supprimer le rappel
          </button>
        )}
      </div>
    </div>
      <div className="flex justify-end gap-2 pt-3 mt-2 border-t flex-shrink-0">
        <Button variant="outline" type="button" onClick={onCancel || (() => { setCreateOpen(false); setEditingTask(null); resetForm(); })}>Annuler</Button>
        <Button type="submit" disabled={isPending} data-testid="button-submit-task">
          {isPending ? "..." : submitLabel}
        </Button>
      </div>
    </form>
  );

  const KanbanCard = ({ task }: { task: Task }) => {
    const [echeanceOpen, setEcheanceOpen] = useState(false);
    const [pendingEcheance, setPendingEcheance] = useState(task.dateEcheance || "");
    const [raisonEcheance, setRaisonEcheance] = useState("");
    const [raisonError, setRaisonError] = useState(false);

    const isClearingDeadlineHighPriority = !pendingEcheance && !!(task.dateEcheance) && task.priorite === "3";

    const handleOpenEcheancePopover = (e: React.MouseEvent) => {
      e.stopPropagation();
      setPendingEcheance(task.dateEcheance || "");
      setRaisonEcheance("");
      setRaisonError(false);
      setEcheanceOpen(true);
    };

    const handleSaveEcheance = async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isClearingDeadlineHighPriority && !raisonEcheance.trim()) {
        setRaisonError(true);
        toast({ variant: "destructive", title: "Raison requise", description: "Veuillez indiquer une raison pour supprimer l'échéance d'une tâche prioritaire." });
        return;
      }
      try {
        await updateMutation.mutateAsync({
          id: task.id,
          dateEcheance: pendingEcheance || null,
          raisonChangementEcheance: raisonEcheance || null,
        } as any);
        setEcheanceOpen(false);
        setRaisonEcheance("");
        setRaisonError(false);
      } catch {
        toast({ variant: "destructive", title: "Erreur", description: "Impossible de modifier l'échéance" });
      }
    };

    const handleCancelEcheance = (e: React.MouseEvent) => {
      e.stopPropagation();
      setEcheanceOpen(false);
      setPendingEcheance(task.dateEcheance || "");
      setRaisonEcheance("");
      setRaisonError(false);
    };

    return (
      <div
        draggable
        onDragStart={e => handleDragStart(e, task.id)}
        className="bg-card border rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
        onClick={() => setDetailTask(task)}
        data-testid={`kanban-card-${task.id}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-1">
              <PriorityIcon priority={task.priorite} />
              <span className="font-medium text-sm truncate" data-testid={`text-task-titre-${task.id}`}>{task.titre}</span>
            </div>
            {(task.labels || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {(task.labels || []).map(l => <LabelBadge key={l} name={l} />)}
              </div>
            )}
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              {(task.emetteurLabel || task.emetteur) && <span data-testid={`text-emetteur-${task.id}`}>{task.emetteurLabel || task.emetteur}</span>}
              {task.assigneeId && (
                <span className="flex items-center gap-1" data-testid={`text-assignee-${task.id}`}>
                  <User className="h-3 w-3" />
                  {getProfileName(task.assigneeId)}
                </span>
              )}
              <Tooltip>
                <Popover open={echeanceOpen} onOpenChange={open => { if (!open) { setEcheanceOpen(false); setPendingEcheance(task.dateEcheance || ""); setRaisonEcheance(""); setRaisonError(false); } }}>
                  <TooltipTrigger asChild>
                    <PopoverTrigger asChild>
                      <button
                        className="flex items-center gap-1 hover:text-foreground transition-colors focus:outline-none"
                        onClick={handleOpenEcheancePopover}
                        data-testid={`btn-quick-edit-echeance-${task.id}`}
                      >
                        {task.dateEcheance ? (
                          <>
                            <Calendar className="h-3 w-3" />
                            <span data-testid={`text-echeance-${task.id}`}>{format(new Date(task.dateEcheance + "T00:00:00"), "dd MMM", { locale: fr })}</span>
                          </>
                        ) : (
                          <CalendarPlus className="h-3 w-3 opacity-30 hover:opacity-60 transition-opacity" data-testid={`text-echeance-empty-${task.id}`} />
                        )}
                      </button>
                    </PopoverTrigger>
                  </TooltipTrigger>
                <PopoverContent side="bottom" align="start" className="w-72 p-3" onClick={e => e.stopPropagation()}>
                  <p className="text-xs font-semibold mb-2">Modifier l'échéance</p>
                  <Input
                    type="date"
                    value={pendingEcheance}
                    onChange={e => { setPendingEcheance(e.target.value); setRaisonError(false); }}
                    className="h-8 text-sm mb-2"
                    data-testid={`input-quick-echeance-${task.id}`}
                    onClick={e => e.stopPropagation()}
                  />
                  {isClearingDeadlineHighPriority && (
                    <div className="space-y-1 mb-2" data-testid={`section-raison-echeance-kanban-${task.id}`}>
                      <Label className="text-xs font-medium text-destructive">
                        Raison de suppression <span className="text-destructive">*</span>
                      </Label>
                      <Textarea
                        placeholder="Pourquoi supprimer l'échéance de cette tâche prioritaire ?"
                        value={raisonEcheance}
                        onChange={e => { setRaisonEcheance(e.target.value); setRaisonError(false); }}
                        className={`h-16 resize-none text-sm${raisonError && !raisonEcheance.trim() ? " border-destructive" : ""}`}
                        data-testid={`input-raison-echeance-kanban-${task.id}`}
                        onClick={e => e.stopPropagation()}
                      />
                      {raisonError && !raisonEcheance.trim() && (
                        <p className="text-xs text-destructive" data-testid={`error-raison-echeance-kanban-${task.id}`}>
                          Une raison est obligatoire pour supprimer l'échéance d'une tâche prioritaire.
                        </p>
                      )}
                    </div>
                  )}
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={handleCancelEcheance}
                      data-testid={`btn-cancel-echeance-kanban-${task.id}`}
                    >
                      Annuler
                    </Button>
                    <Button
                      size="sm"
                      className="h-7 text-xs"
                      onClick={handleSaveEcheance}
                      disabled={updateMutation.isPending || (isClearingDeadlineHighPriority && !raisonEcheance.trim())}
                      data-testid={`btn-save-echeance-kanban-${task.id}`}
                    >
                      {updateMutation.isPending ? "..." : "Enregistrer"}
                    </Button>
                  </div>
                </PopoverContent>
                </Popover>
                <TooltipContent side="top" className="text-xs">
                  {task.dateEcheance ? "Modifier l'échéance" : "Ajouter une échéance"}
                </TooltipContent>
              </Tooltip>
            </div>
            {viewerLevel >= ROLE_LEVEL.manager && assignableProfiles.length > 0 && (
              <div className="mt-2" onClick={e => e.stopPropagation()}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 text-xs px-2 border-dashed text-muted-foreground hover:text-foreground"
                      data-testid={`btn-quick-assign-${task.id}`}
                    >
                      <User className="h-3 w-3 mr-1" />
                      {task.assigneeId ? "Réassigner" : "Assigner"}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-48">
                    {assignableProfiles.map((p: Profile) => (
                      <DropdownMenuItem
                        key={p.id}
                        onClick={() => handleQuickAssign(task.id, p.id)}
                        data-testid={`quick-assign-${task.id}-${p.id}`}
                        className={task.assigneeId === p.id ? "bg-muted font-medium" : ""}
                      >
                        <User className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
                        {p.firstName} {p.lastName}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            {task.rappelDate && (
              <Bell
                className={`h-3.5 w-3.5 ${new Date(task.rappelDate) <= new Date() ? "text-amber-500" : "text-muted-foreground/50"}`}
                title={`Rappel ${new Date(task.rappelDate).toLocaleDateString('fr-FR')}`}
                data-testid={`icon-rappel-${task.id}`}
              />
            )}
            {echeanceChanges[task.id] && (() => {
              const change = echeanceChanges[task.id];
              const history = [...(change.history ?? [])].reverse();
              return (
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      className="focus:outline-none"
                      data-testid={`icon-echeance-changed-${task.id}`}
                      onClick={e => e.stopPropagation()}
                    >
                      <CalendarClock className="h-3.5 w-3.5 text-blue-500 cursor-pointer hover:text-blue-700 transition-colors" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent side="left" className="w-72 p-0" onClick={e => e.stopPropagation()}>
                    <div className="px-3 py-2 border-b">
                      <p className="text-xs font-semibold">Historique des échéances ({change.count}x)</p>
                    </div>
                    <div className="max-h-48 overflow-y-auto divide-y">
                      {history.map((h, i) => (
                        <div key={i} className="px-3 py-2">
                          <p className="text-xs text-foreground">{h.contenu}</p>
                          {h.date && (
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              {format(new Date(h.date), "dd MMM yyyy 'à' HH:mm", { locale: fr })}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              );
            })()}
            <GripVertical className="h-4 w-4 text-muted-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5" />
          </div>
        </div>
      </div>
    );
  };

  const ListRowDeadlineCell = ({ task }: { task: Task }) => {
    const [echeanceOpen, setEcheanceOpen] = useState(false);
    const [pendingEcheance, setPendingEcheance] = useState(task.dateEcheance || "");
    const [raisonEcheance, setRaisonEcheance] = useState("");
    const [raisonError, setRaisonError] = useState(false);

    const isClearingDeadlineHighPriority = !pendingEcheance && !!(task.dateEcheance) && task.priorite === "3";

    const handleOpenEcheancePopover = (e: React.MouseEvent) => {
      e.stopPropagation();
      setPendingEcheance(task.dateEcheance || "");
      setRaisonEcheance("");
      setRaisonError(false);
      setEcheanceOpen(true);
    };

    const handleSaveEcheance = async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isClearingDeadlineHighPriority && !raisonEcheance.trim()) {
        setRaisonError(true);
        toast({ variant: "destructive", title: "Raison requise", description: "Veuillez indiquer une raison pour supprimer l'échéance d'une tâche prioritaire." });
        return;
      }
      try {
        await updateMutation.mutateAsync({
          id: task.id,
          dateEcheance: pendingEcheance || null,
          raisonChangementEcheance: raisonEcheance || null,
        } as any);
        setEcheanceOpen(false);
        setRaisonEcheance("");
        setRaisonError(false);
      } catch {
        toast({ variant: "destructive", title: "Erreur", description: "Impossible de modifier l'échéance" });
      }
    };

    const handleCancelEcheance = (e: React.MouseEvent) => {
      e.stopPropagation();
      setEcheanceOpen(false);
      setPendingEcheance(task.dateEcheance || "");
      setRaisonEcheance("");
      setRaisonError(false);
    };

    const change = echeanceChanges[task.id];
    const history = change ? [...(change.history ?? [])].reverse() : [];

    return (
      <div className="flex items-center gap-1.5">
        {task.dateEcheance ? (
          <span className="text-sm">{format(new Date(task.dateEcheance + "T00:00:00"), "dd MMM yyyy", { locale: fr })}</span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
        <Tooltip>
          <Popover open={echeanceOpen} onOpenChange={open => { if (!open) { setEcheanceOpen(false); setPendingEcheance(task.dateEcheance || ""); setRaisonEcheance(""); setRaisonError(false); } }}>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <button
                  className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors focus:outline-none"
                  onClick={handleOpenEcheancePopover}
                  data-testid={`btn-quick-edit-echeance-list-${task.id}`}
                >
                  {task.dateEcheance ? (
                    <Calendar className="h-3.5 w-3.5" />
                  ) : (
                    <CalendarPlus className="h-3.5 w-3.5 opacity-40 hover:opacity-70 transition-opacity" data-testid={`text-echeance-empty-list-${task.id}`} />
                  )}
                </button>
              </PopoverTrigger>
            </TooltipTrigger>
            <PopoverContent side="bottom" align="start" className="w-72 p-3" onClick={e => e.stopPropagation()}>
              <p className="text-xs font-semibold mb-2">Modifier l'échéance</p>
              <Input
                type="date"
                value={pendingEcheance}
                onChange={e => { setPendingEcheance(e.target.value); setRaisonError(false); }}
                className="h-8 text-sm mb-2"
                data-testid={`input-quick-echeance-list-${task.id}`}
                onClick={e => e.stopPropagation()}
              />
              {isClearingDeadlineHighPriority && (
                <div className="space-y-1 mb-2" data-testid={`section-raison-echeance-list-${task.id}`}>
                  <Label className="text-xs font-medium text-destructive">
                    Raison de suppression <span className="text-destructive">*</span>
                  </Label>
                  <Textarea
                    placeholder="Pourquoi supprimer l'échéance de cette tâche prioritaire ?"
                    value={raisonEcheance}
                    onChange={e => { setRaisonEcheance(e.target.value); setRaisonError(false); }}
                    className={`h-16 resize-none text-sm${raisonError && !raisonEcheance.trim() ? " border-destructive" : ""}`}
                    data-testid={`input-raison-echeance-list-${task.id}`}
                    onClick={e => e.stopPropagation()}
                  />
                  {raisonError && !raisonEcheance.trim() && (
                    <p className="text-xs text-destructive" data-testid={`error-raison-echeance-list-${task.id}`}>
                      Une raison est obligatoire pour supprimer l'échéance d'une tâche prioritaire.
                    </p>
                  )}
                </div>
              )}
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={handleCancelEcheance}
                  data-testid={`btn-cancel-echeance-list-${task.id}`}
                >
                  Annuler
                </Button>
                <Button
                  size="sm"
                  className="h-7 text-xs"
                  onClick={handleSaveEcheance}
                  disabled={updateMutation.isPending || (isClearingDeadlineHighPriority && !raisonEcheance.trim())}
                  data-testid={`btn-save-echeance-list-${task.id}`}
                >
                  {updateMutation.isPending ? "..." : "Enregistrer"}
                </Button>
              </div>
            </PopoverContent>
          </Popover>
          <TooltipContent side="top" className="text-xs">
            {task.dateEcheance ? "Modifier l'échéance" : "Ajouter une échéance"}
          </TooltipContent>
        </Tooltip>
        {change && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                className="focus:outline-none"
                data-testid={`icon-echeance-changed-list-${task.id}`}
                onClick={e => e.stopPropagation()}
              >
                <CalendarClock className="h-3.5 w-3.5 text-blue-500 cursor-pointer hover:text-blue-700 transition-colors flex-shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent side="top" className="w-72 p-0" onClick={e => e.stopPropagation()}>
              <div className="px-3 py-2 border-b">
                <p className="text-xs font-semibold">Historique des échéances ({change.count}x)</p>
              </div>
              <div className="max-h-48 overflow-y-auto divide-y">
                {history.map((h, i) => (
                  <div key={i} className="px-3 py-2">
                    <p className="text-xs text-foreground">{h.contenu}</p>
                    {h.date && (
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {format(new Date(h.date), "dd MMM yyyy 'à' HH:mm", { locale: fr })}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold" data-testid="text-page-title">Suivi des actions</h1>
          <p className="text-muted-foreground mt-1">Pilotage des actions et points de suivi</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={showArchived ? "default" : "outline"}
            size="sm"
            onClick={() => setShowArchived(!showArchived)}
            data-testid="button-toggle-archives-tasks"
          >
            <Archive className="h-4 w-4 mr-2" />
            {showArchived ? "Vue active" : "Voir les archives"}
          </Button>
          {!showArchived && (
            <>
              <div className="flex items-center border rounded-lg overflow-hidden">
                <Button
                  variant={viewMode === "kanban" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("kanban")}
                  className="rounded-none"
                  data-testid="btn-view-kanban"
                >
                  <LayoutGrid className="h-4 w-4 mr-1" />
                  Kanban
                </Button>
                <Button
                  variant={viewMode === "list" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("list")}
                  className="rounded-none"
                  data-testid="btn-view-list"
                >
                  <List className="h-4 w-4 mr-1" />
                  Liste
                </Button>
              </div>
              <Button onClick={openCreate} className="shadow-lg shadow-primary/20" data-testid="button-create-task">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle action
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Rechercher..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="input-search-tasks" />
        </div>
        <Select value={filterAssignee} onValueChange={setFilterAssignee}>
          <SelectTrigger className="w-[200px]" data-testid="filter-assignee">
            <SelectValue placeholder="Voir les tâches de :" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Workspace (tous)</SelectItem>
            {activeProfiles.map((p: Profile) => (
              <SelectItem key={p.id} value={p.id}>{p.firstName} {p.lastName}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterLabel} onValueChange={setFilterLabel}>
          <SelectTrigger className="w-[160px]" data-testid="filter-label"><SelectValue placeholder="Label" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les labels</SelectItem>
            {allLabels.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="w-[140px]" data-testid="filter-priority"><SelectValue placeholder="Priorité" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes</SelectItem>
            <SelectItem value="3">🔴 Fort</SelectItem>
            <SelectItem value="2">🟠 Moyen</SelectItem>
            <SelectItem value="1">🟢 Faible</SelectItem>
          </SelectContent>
        </Select>
        {viewMode === "list" && (
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[150px]" data-testid="filter-status"><SelectValue placeholder="Statut" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              {Object.entries(STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={sortBy} onValueChange={v => setSortBy(v as any)}>
          <SelectTrigger className="w-[150px]" data-testid="sort-by"><SelectValue placeholder="Trier par" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="priority">Priorité</SelectItem>
            <SelectItem value="echeance">Échéance</SelectItem>
            <SelectItem value="creation">Date création</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      ) : showArchived ? (
        <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/40">
                <TableHead className="w-8"></TableHead>
                <TableHead>Titre</TableHead>
                <TableHead>Émetteur</TableHead>
                <TableHead>Assigné</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Date archivage</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center h-32">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <Archive className="w-8 h-8" />
                      <p>Aucune action archivée</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredTasks.map((task: Task) => (
                <TableRow key={task.id} className="cursor-pointer hover:bg-muted/50 opacity-75" data-testid={`row-task-archived-${task.id}`}>
                  <TableCell><PriorityIcon priority={task.priorite} /></TableCell>
                  <TableCell className="font-medium" onClick={() => setDetailTask(task)}>
                    {task.titre}
                    <Badge variant="secondary" className="ml-2 text-[10px] bg-gray-200 text-gray-600">Archivé</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{task.emetteurLabel || task.emetteur || "—"}</TableCell>
                  <TableCell className="text-sm">{getProfileName(task.assigneeId)}</TableCell>
                  <TableCell>
                    <span className="text-xs">{STATUS_LABELS[task.status] || task.status}</span>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {(task as any).dateArchivage ? format(new Date((task as any).dateArchivage), "dd/MM/yyyy", { locale: fr }) : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => handleUnarchiveTask(task.id)} title="Désarchiver" data-testid={`button-unarchive-task-${task.id}`}>
                      <ArchiveRestore className="h-4 w-4 text-blue-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : viewMode === "kanban" ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="kanban-board">
          {STATUS_COLUMNS.map(status => {
            const columnTasks = filteredTasks.filter((t: Task) => t.status === status);
            const statusColors: Record<string, string> = {
              a_faire: "border-red-300 dark:border-red-800",
              en_cours: "border-amber-300 dark:border-amber-800",
              fait: "border-emerald-300 dark:border-emerald-800",
            };
            const headerColors: Record<string, string> = {
              a_faire: "bg-red-50 dark:bg-red-950/30",
              en_cours: "bg-amber-50 dark:bg-amber-950/30",
              fait: "bg-emerald-50 dark:bg-emerald-950/30",
            };
            return (
              <div
                key={status}
                className={`rounded-xl border-2 ${statusColors[status]} bg-muted/20 min-h-[400px] flex flex-col`}
                onDragOver={handleDragOver}
                onDrop={e => handleDrop(e, status)}
                data-testid={`kanban-column-${status}`}
              >
                <div className={`p-3 rounded-t-xl ${headerColors[status]} border-b flex items-center justify-between`}>
                  <div className="flex items-center gap-2">
                    <CircleDot className="h-4 w-4" />
                    <span className="font-semibold text-sm">{STATUS_LABELS[status]}</span>
                  </div>
                  <Badge variant="secondary" className="text-xs" data-testid={`count-${status}`}>
                    {columnTasks.length}
                  </Badge>
                </div>
                <div className="p-2 space-y-2 flex-1">
                  {columnTasks.map((task: Task) => (
                    <KanbanCard key={task.id} task={task} />
                  ))}
                  {columnTasks.length === 0 && (
                    <div className="flex items-center justify-center h-24 text-muted-foreground text-xs">
                      Glissez une carte ici
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/40">
                <TableHead className="w-8"></TableHead>
                <TableHead>Titre</TableHead>
                <TableHead>Émetteur</TableHead>
                <TableHead>Assigné</TableHead>
                <TableHead>Labels</TableHead>
                <TableHead>Priorité</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Échéance</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center h-32">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <ClipboardList className="w-8 h-8" />
                      <p>Aucune action trouvée</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredTasks.map((task: Task) => (
                <TableRow key={task.id} className="cursor-pointer hover:bg-muted/50" data-testid={`row-task-${task.id}`}>
                  <TableCell><PriorityIcon priority={task.priorite} /></TableCell>
                  <TableCell className="font-medium" onClick={() => setDetailTask(task)} data-testid={`text-task-titre-${task.id}`}>
                    {task.titre}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{task.emetteurLabel || task.emetteur || "—"}</TableCell>
                  <TableCell className="text-sm">
                    {viewerLevel >= ROLE_LEVEL.manager && assignableProfiles.length > 0 ? (
                      <Select
                        value={task.assigneeId ?? ""}
                        onValueChange={v => handleQuickAssign(task.id, v)}
                      >
                        <SelectTrigger
                          className="w-[140px] h-7 text-xs border-dashed text-muted-foreground"
                          data-testid={`select-quick-assign-${task.id}`}
                          onClick={e => e.stopPropagation()}
                        >
                          <SelectValue placeholder="Assigner…" />
                        </SelectTrigger>
                        <SelectContent>
                          {assignableProfiles.map((p: Profile) => (
                            <SelectItem key={p.id} value={p.id} data-testid={`option-assign-${task.id}-${p.id}`}>
                              {p.firstName} {p.lastName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      getProfileName(task.assigneeId)
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(task.labels || []).map(l => <LabelBadge key={l} name={l} />)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={`text-xs font-medium ${PRIORITY_CONFIG[task.priorite]?.color || ""}`}>
                      {PRIORITY_CONFIG[task.priorite]?.label || "—"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Select value={task.status} onValueChange={v => handleStatusChange(task.id, v)}>
                      <SelectTrigger className="w-[110px] h-7 text-xs" data-testid={`select-status-${task.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-sm" onClick={e => e.stopPropagation()}>
                    <ListRowDeadlineCell task={task} />
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`btn-actions-${task.id}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setDetailTask(task)} data-testid={`btn-view-${task.id}`}>
                          <Eye className="h-4 w-4 mr-2" />Voir
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openEdit(task)} data-testid={`btn-edit-${task.id}`}>
                          <Pencil className="h-4 w-4 mr-2" />Modifier
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleArchiveTask(task.id)} data-testid={`btn-archive-${task.id}`}>
                          <Archive className="h-4 w-4 mr-2" />Archiver
                        </DropdownMenuItem>
                        {(profile as any)?.accessRole === 'admin' || (profile as any)?.accessRole === 'super_admin' ? (
                          <DropdownMenuItem onClick={() => handleDelete(task.id)} className="text-destructive" data-testid={`btn-delete-${task.id}`}>
                            <Trash2 className="h-4 w-4 mr-2" />Supprimer
                          </DropdownMenuItem>
                        ) : null}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={v => { if (!v) handleCancelCreate(); }}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col overflow-hidden">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Nouvelle action</DialogTitle>
          </DialogHeader>
          {renderTaskForm(handleSubmit, "Créer", createMutation.isPending, draftTaskId || undefined, handleCancelCreate)}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingTask} onOpenChange={v => { if (!v) { setEditingTask(null); resetForm(); } }}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col overflow-hidden">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Modifier l'action</DialogTitle>
          </DialogHeader>
          {renderTaskForm(handleUpdate, "Enregistrer", updateMutation.isPending, editingTask?.id, () => { setEditingTask(null); resetForm(); })}
        </DialogContent>
      </Dialog>

      {detailTask && (
        <TaskDetailPanel
          task={detailTask}
          onClose={() => setDetailTask(null)}
          onEdit={() => { openEdit(detailTask); setDetailTask(null); }}
          onDelete={() => handleDelete(detailTask.id)}
          onStatusChange={handleStatusChange}
          getProfileName={getProfileName}
          profile={profile}
          allProfiles={profiles || []}
        />
      )}

      <AlertDialog open={!!archiveConfirmId} onOpenChange={(open) => !open && setArchiveConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archiver cette action ?</AlertDialogTitle>
            <AlertDialogDescription>
              L'action est terminée. Voulez-vous l'archiver ? Elle disparaîtra de la vue active mais restera consultable dans les archives.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Non, garder visible</AlertDialogCancel>
            <AlertDialogAction onClick={() => archiveConfirmId && handleArchiveTask(archiveConfirmId)}>
              Oui, archiver
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TaskDetailPanel({
  task,
  onClose,
  onEdit,
  onDelete,
  onStatusChange,
  getProfileName,
  profile,
  allProfiles,
}: {
  task: Task;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onStatusChange: (id: string, status: string) => void;
  getProfileName: (id: string | null) => string;
  profile: Profile | null;
  allProfiles: Profile[];
}) {
  const [, navigate] = useLocation();
  const { data: comments = [], isLoading: commentsLoading } = useTaskComments(task.id);
  const createComment = useCreateTaskComment();
  const deleteComment = useDeleteTaskComment();
  const updateTask = useUpdateTask();
  const { toast } = useToast();
  const [commentText, setCommentText] = useState("");
  const [mentionedIds, setMentionedIds] = useState<string[]>([]);
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showEcheanceHistory, setShowEcheanceHistory] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editingEcheance, setEditingEcheance] = useState(false);
  const [pendingEcheance, setPendingEcheance] = useState("");
  const [raisonEcheance, setRaisonEcheance] = useState("");

  const isClearingDeadlineHighPriority =
    editingEcheance && !pendingEcheance && !!(task.dateEcheance) && task.priorite === "3";

  const handleSaveEcheance = async () => {
    if (isClearingDeadlineHighPriority && !raisonEcheance.trim()) {
      toast({ variant: "destructive", title: "Raison requise", description: "Veuillez indiquer une raison pour supprimer l'échéance d'une tâche prioritaire." });
      return;
    }
    try {
      await updateTask.mutateAsync({
        id: task.id,
        dateEcheance: pendingEcheance || null,
        raisonChangementEcheance: raisonEcheance || null,
      } as any);
      setEditingEcheance(false);
      setRaisonEcheance("");
    } catch {
      toast({ variant: "destructive", title: "Erreur", description: "Impossible de modifier l'échéance" });
    }
  };

  const handleCancelEcheance = () => {
    setEditingEcheance(false);
    setPendingEcheance(task.dateEcheance || "");
    setRaisonEcheance("");
  };

  const handleTransformToMission = () => {
    const params = new URLSearchParams();
    params.set("fromTask", task.id);
    params.set("eventName", task.titre);
    if (task.notes) params.set("description", task.notes);

    const labels = task.labels || [];
    let missionType = "demonstration";
    for (const label of labels) {
      if (LABEL_TO_MISSION_TYPE[label]) {
        missionType = LABEL_TO_MISSION_TYPE[label];
        break;
      }
    }
    params.set("missionType", missionType);

    if (task.assigneeId) {
      const assignee = allProfiles.find(p => p.id === task.assigneeId);
      if (assignee?.role === "technicien") {
        params.set("technicianId", task.assigneeId);
      } else if (assignee?.role === "commercial") {
        params.set("commercialId", task.assigneeId);
      }
    }

    if (task.emetteurId && task.emetteurType === "client") {
      params.set("tiersType", "client");
      params.set("tiersId", task.emetteurId);
    } else if (task.emetteurId && task.emetteurType === "prospect") {
      params.set("tiersType", "prospect");
      params.set("tiersId", task.emetteurId);
    } else if (task.emetteurId && task.emetteurType === "revendeur") {
      params.set("tiersType", "revendeur");
      params.set("tiersId", task.emetteurId);
    }

    onClose();
    navigate(`/missions?${params.toString()}`);
  };

  const handleAddComment = async () => {
    if (!commentText.trim() && !attachmentFile) return;
    let pieceJointeUrl: string | null = null;
    try {
      if (attachmentFile) {
        setUploading(true);
        const formData = new FormData();
        formData.append("file", attachmentFile);
        const res = await authFetch("/api/upload/tasks", {
          method: "POST",
          body: formData,
        });
        setUploading(false);
        if (res.ok) {
          const data = await res.json();
          pieceJointeUrl = data.url;
        } else {
          toast({ variant: "destructive", title: "Erreur upload", description: "Impossible d'envoyer la pièce jointe." });
          return;
        }
      }
      await createComment.mutateAsync({
        taskId: task.id,
        contenu: commentText.trim() || (attachmentFile ? `Pièce jointe: ${attachmentFile.name}` : ""),
        pieceJointeUrl,
        mentionedUserIds: mentionedIds.length ? mentionedIds : undefined,
      } as any);
      setCommentText("");
      setMentionedIds([]);
      setAttachmentFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err: any) {
      setUploading(false);
      toast({
        variant: "destructive",
        title: "Erreur envoi commentaire",
        description: err?.message || "Une erreur est survenue. Veuillez réessayer.",
      });
    }
  };

  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-[700px] max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between pr-6">
            <DialogTitle className="text-lg">{task.titre}</DialogTitle>
            <div className="flex items-center gap-1">
              {task.status !== "fait" && !task.missionId && (
                <Button variant="ghost" size="sm" onClick={handleTransformToMission} className="text-primary" title="Transformer en mission" data-testid="btn-transform-mission">
                  <Rocket className="h-4 w-4" />
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={onEdit} data-testid="btn-detail-edit">
                <Pencil className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={onDelete} className="text-destructive" data-testid="btn-detail-delete">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 overflow-y-auto">
          <div className="space-y-5 pr-2">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground text-xs">Émetteur</span>
                <div className="flex items-center gap-1.5">
                  <p className="font-medium" data-testid="detail-emetteur">{task.emetteurLabel || task.emetteur || "—"}</p>
                  {task.emetteurType && task.emetteurType !== "libre" && (
                    <Badge variant="outline" className="text-[10px] h-4 px-1">
                      {task.emetteurType === "client" ? "Client" : task.emetteurType === "revendeur" ? "Revendeur" : task.emetteurType === "prospect" ? "Prospect" : task.emetteurType === "equipe" ? "Équipe" : task.emetteurType}
                    </Badge>
                  )}
                </div>
              </div>
              <div>
                <span className="text-muted-foreground text-xs">Assigné à</span>
                <p className="font-medium" data-testid="detail-assignee">{getProfileName(task.assigneeId)}</p>
              </div>
              <div>
                <span className="text-muted-foreground text-xs">Priorité</span>
                <div className="flex items-center gap-1.5">
                  <PriorityIcon priority={task.priorite} />
                  <span className="font-medium">{PRIORITY_CONFIG[task.priorite]?.label || "—"}</span>
                </div>
              </div>
              <div>
                <span className="text-muted-foreground text-xs">Statut</span>
                <Select value={task.status} onValueChange={v => onStatusChange(task.id, v)}>
                  <SelectTrigger className="w-[120px] h-7 text-xs mt-0.5" data-testid="detail-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-muted-foreground text-xs">Échéance</span>
                  {!editingEcheance && (
                    <button
                      onClick={() => { setPendingEcheance(task.dateEcheance || ""); setEditingEcheance(true); }}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      title="Modifier l'échéance"
                      data-testid="btn-edit-echeance"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                  )}
                </div>
                {!editingEcheance ? (
                  <p className="font-medium" data-testid="detail-echeance">
                    {task.dateEcheance ? format(new Date(task.dateEcheance + "T00:00:00"), "dd MMMM yyyy", { locale: fr }) : "—"}
                  </p>
                ) : (
                  <div className="mt-1 space-y-2" data-testid="section-inline-echeance">
                    <Input
                      type="date"
                      value={pendingEcheance}
                      onChange={e => { setPendingEcheance(e.target.value); setRaisonEcheance(""); }}
                      className="h-8 text-sm"
                      data-testid="input-inline-echeance"
                    />
                    {isClearingDeadlineHighPriority && (
                      <div className="space-y-1" data-testid="section-raison-echeance-inline">
                        <Label className="text-xs">
                          Raison de la suppression{" "}
                          <span className="text-destructive font-normal">(requis)</span>
                        </Label>
                        <Textarea
                          value={raisonEcheance}
                          onChange={e => setRaisonEcheance(e.target.value)}
                          placeholder="Pourquoi supprimer l'échéance ?"
                          className={`h-16 resize-none text-sm${!raisonEcheance.trim() ? " border-destructive" : ""}`}
                          data-testid="input-raison-echeance-inline"
                        />
                        {!raisonEcheance.trim() && (
                          <p className="text-xs text-destructive" data-testid="error-raison-echeance-inline">
                            Une raison est obligatoire pour supprimer l'échéance d'une tâche prioritaire.
                          </p>
                        )}
                      </div>
                    )}
                    <div className="flex gap-1.5">
                      <Button
                        size="sm"
                        className="h-7 text-xs"
                        onClick={handleSaveEcheance}
                        disabled={updateTask.isPending || (isClearingDeadlineHighPriority && !raisonEcheance.trim())}
                        data-testid="btn-save-echeance"
                      >
                        {updateTask.isPending ? "..." : "Enregistrer"}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={handleCancelEcheance}
                        data-testid="btn-cancel-echeance"
                      >
                        Annuler
                      </Button>
                    </div>
                  </div>
                )}
                {(() => {
                  const echeanceComments = comments.filter(
                    (c: TaskComment) => c.isSystem && c.contenu.startsWith("Échéance")
                  );
                  if (echeanceComments.length === 0) return null;
                  return (
                    <div className="mt-1.5">
                      <button
                        onClick={() => setShowEcheanceHistory(v => !v)}
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="btn-toggle-echeance-history"
                      >
                        <CalendarClock className="h-3 w-3" />
                        <span>Historique de l'échéance ({echeanceComments.length})</span>
                        <ChevronDown className={`h-3 w-3 transition-transform ${showEcheanceHistory ? "rotate-180" : ""}`} />
                      </button>
                      {showEcheanceHistory && (
                        <div className="mt-1.5 space-y-1" data-testid="echeance-history">
                          {[...echeanceComments].sort((a, b) =>
                            new Date(a.createdAt ?? 0).getTime() - new Date(b.createdAt ?? 0).getTime()
                          ).map((c: TaskComment) => (
                            <div key={c.id} className="flex items-start gap-1.5 py-1 px-2 rounded bg-muted/40 border border-dashed border-muted-foreground/20" data-testid={`echeance-history-entry-${c.id}`}>
                              <CalendarClock className="h-3 w-3 text-muted-foreground flex-shrink-0 mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <span className="text-xs text-muted-foreground">{c.contenu}</span>
                                <div className="text-[10px] text-muted-foreground/60 mt-0.5">
                                  {c.auteur} · {c.createdAt ? format(new Date(c.createdAt), "dd MMM yyyy HH:mm", { locale: fr }) : ""}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              <div>
                <span className="text-muted-foreground text-xs">Créée le</span>
                <p className="font-medium">
                  {task.createdAt ? format(new Date(task.createdAt), "dd MMM yyyy", { locale: fr }) : "—"}
                </p>
              </div>
            </div>

            {(task.labels || []).length > 0 && (
              <div>
                <span className="text-muted-foreground text-xs">Labels</span>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {(task.labels || []).map(l => <LabelBadge key={l} name={l} />)}
                </div>
              </div>
            )}

            {task.notes && (
              <div>
                <span className="text-muted-foreground text-xs">Notes</span>
                <div className="mt-1 p-3 rounded-lg bg-muted/50 border text-sm whitespace-pre-wrap" data-testid="detail-notes" onCopy={handlePlainTextCopy}>
                  {task.notes}
                </div>
              </div>
            )}

            <TaskContactsSection taskId={task.id} />

            <TaskPiecesSection taskId={task.id} />

            <div className="border-t pt-4">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
                <span className="font-semibold text-sm">Activité</span>
                <Badge variant="secondary" className="text-xs">{comments.length}</Badge>
              </div>

              {commentsLoading ? (
                <p className="text-xs text-muted-foreground">Chargement...</p>
              ) : (
                <div className="space-y-3">
                  {comments.map((c: TaskComment) => (
                    c.isSystem ? (
                      <div key={c.id} className="flex items-center gap-2 py-1 px-2 rounded-md bg-muted/40 border border-dashed border-muted-foreground/20" data-testid={`activity-${c.id}`}>
                        {c.contenu.startsWith("Statut changé")
                          ? <ArrowRightLeft className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                          : c.contenu.startsWith("Priorité changée")
                          ? <Flag className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                          : c.contenu.startsWith("Échéance changée")
                          ? <CalendarClock className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                          : <UserCheck className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />}
                        <span className="text-xs text-muted-foreground flex-1">{c.contenu}</span>
                        <span className="text-xs text-muted-foreground/60 whitespace-nowrap">
                          {c.auteur} · {c.createdAt ? format(new Date(c.createdAt), "dd MMM yyyy HH:mm", { locale: fr }) : ""}
                        </span>
                      </div>
                    ) : (
                    <div key={c.id} className="flex gap-3 group" data-testid={`comment-${c.id}`}>
                      <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-medium text-primary">
                          {(c.auteur || "?")[0].toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{c.auteur}</span>
                          <span className="text-xs text-muted-foreground">
                            {c.createdAt ? format(new Date(c.createdAt), "dd MMM yyyy HH:mm", { locale: fr }) : ""}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity ml-auto"
                            onClick={() => deleteComment.mutate({ id: c.id, taskId: task.id })}
                            data-testid={`btn-delete-comment-${c.id}`}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                        <p className="text-sm mt-0.5 whitespace-pre-wrap">{renderWithMentions(c.contenu, allProfiles)}</p>
                        {c.pieceJointeUrl && (
                          <a href={c.pieceJointeUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1 mt-1">
                            <Paperclip className="h-3 w-3" />Pièce jointe
                          </a>
                        )}
                      </div>
                    </div>
                    )
                  ))}
                </div>
              )}

              <div className="mt-4 space-y-2">
                {attachmentFile && (
                  <div className="flex items-center gap-2 text-xs bg-muted/50 px-2 py-1 rounded">
                    <Paperclip className="h-3 w-3" />
                    <span className="truncate flex-1">{attachmentFile.name}</span>
                    <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => { setAttachmentFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}>
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                )}
                <div className="flex gap-2">
                  <MentionTextarea
                    value={commentText}
                    onChange={setCommentText}
                    onMentionedIdsChange={setMentionedIds}
                    profiles={task.taskType === "personnelle" ? [] : allProfiles}
                    placeholder="Ajouter un commentaire… (@ pour mentionner)"
                    rows={2}
                    className="flex-1"
                    data-testid="input-comment"
                  />
                  <div className="flex flex-col gap-1 self-end">
                    <input type="file" ref={fileInputRef} className="hidden" onChange={e => { if (e.target.files?.[0]) setAttachmentFile(e.target.files[0]); }} data-testid="input-attachment" />
                    <Button variant="outline" size="icon" onClick={() => fileInputRef.current?.click()} data-testid="btn-attach">
                      <Paperclip className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      onClick={handleAddComment}
                      disabled={(!commentText.trim() && !attachmentFile) || createComment.isPending || uploading}
                      data-testid="btn-submit-comment"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

function EmetteurSelect({
  value,
  freeText,
  forceMode,
  options,
  onSelect,
  "data-testid": testId,
}: {
  value: string | null;
  freeText: string;
  forceMode?: "search" | "libre";
  options: { value: string; label: string; sublabel?: string; type: string }[];
  onSelect: (id: string | null, type: string, label: string) => void;
  "data-testid"?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const mode = forceMode || (value ? "search" : (freeText ? "libre" : "search"));

  const selected = value ? options.find(o => o.value === value) : null;

  const filtered = options.filter(o => {
    if (!search) return true;
    const s = search.toLowerCase();
    return o.label.toLowerCase().includes(s) || (o.sublabel && o.sublabel.toLowerCase().includes(s));
  });

  const typeBadge = (type: string) => {
    if (type === "client") return <Badge variant="outline" className="text-[10px] h-4 px-1 ml-auto shrink-0">Client</Badge>;
    if (type === "revendeur") return <Badge variant="outline" className="text-[10px] h-4 px-1 ml-auto shrink-0 border-amber-300 text-amber-700">Revendeur</Badge>;
    if (type === "prospect") return <Badge variant="outline" className="text-[10px] h-4 px-1 ml-auto shrink-0 border-blue-300 text-blue-700">Prospect</Badge>;
    if (type === "equipe") return <Badge variant="outline" className="text-[10px] h-4 px-1 ml-auto shrink-0 border-green-300 text-green-700">Équipe</Badge>;
    return null;
  };

  if (mode === "libre") {
    return (
      <Input
        value={freeText}
        onChange={e => onSelect(null, "libre", e.target.value)}
        placeholder="Saisie libre (ex: Pudu, Salon...)"
        data-testid={testId}
      />
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className="w-full justify-between font-normal h-10"
          type="button"
        >
          <span className="truncate flex-1 text-left">
            {selected ? (
              <span className="flex items-center gap-1.5">
                <span className="truncate">{selected.label}</span>
                {typeBadge(selected.type)}
              </span>
            ) : (
              <span className="text-muted-foreground">Client, revendeur, prospect, équipe...</span>
            )}
          </span>
          <span className="flex items-center gap-1 shrink-0 ml-1">
            {selected && (
              <X
                className="h-3.5 w-3.5 opacity-50 hover:opacity-100 cursor-pointer"
                onClick={(e) => { e.stopPropagation(); onSelect(null, "libre", ""); }}
              />
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        style={{ width: "max(var(--radix-popover-trigger-width), 22rem)" }}
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Rechercher client, revendeur, prospect, équipe..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>Aucun résultat</CommandEmpty>
            <CommandGroup>
              {filtered.map(option => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => {
                    const [type, id] = option.value.split("::");
                    onSelect(id, type, option.label);
                    setOpen(false);
                    setSearch("");
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4 shrink-0", value === option.value ? "opacity-100" : "opacity-0")} />
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="text-sm font-semibold leading-tight">{option.label}</span>
                      {option.sublabel && <span className="text-xs text-muted-foreground leading-tight mt-0.5">{option.sublabel}</span>}
                    </div>
                    {typeBadge(option.type)}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function TaskContactsSection({ taskId }: { taskId: string }) {
  const queryClient = useQueryClient();
  const { data: linked = [], isLoading } = useQuery<{ id: string; contactId: string; role?: string | null; contact: any }[]>({
    queryKey: ["/api/tasks", taskId, "contacts"],
    queryFn: async () => {
      const res = await authFetch(`/api/tasks/${taskId}/contacts`);
      if (!res.ok) return [];
      return res.json();
    },
  });

  const addContact = useMutation({
    mutationFn: async (contactId: string) => {
      const res = await authFetch(`/api/tasks/${taskId}/contacts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contactId }),
      });
      if (!res.ok) throw new Error("Erreur");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/tasks", taskId, "contacts"] }),
  });

  const removeContact = useMutation({
    mutationFn: async (contactId: string) => {
      const res = await authFetch(`/api/tasks/${taskId}/contacts/${contactId}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Erreur");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/tasks", taskId, "contacts"] }),
  });

  const linkedIds = linked.map(l => l.contactId);

  const handleChange = (newIds: string[]) => {
    const added = newIds.filter(id => !linkedIds.includes(id));
    const removed = linkedIds.filter(id => !newIds.includes(id));
    for (const id of added) addContact.mutate(id);
    for (const id of removed) removeContact.mutate(id);
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <User className="h-4 w-4 text-muted-foreground" />
        <span className="font-semibold text-sm">Contacts liés</span>
        {linked.length > 0 && (
          <Badge variant="secondary" className="text-xs">{linked.length}</Badge>
        )}
      </div>
      <ContactSelector
        value={linkedIds}
        onChange={handleChange}
        multiple={true}
        allowCreate={true}
        placeholder="Ajouter un contact à cette tâche..."
      />
    </div>
  );
}

function TaskPiecesSection({ taskId }: { taskId: string }) {
  const { data: pieces = [], isLoading } = useTaskPieces(taskId);
  const { data: productsList } = useProducts();
  const createPiece = useCreateTaskPiece();
  const deletePiece = useDeleteTaskPiece();
  const [addingPiece, setAddingPiece] = useState(false);

  const productOptions = useMemo(() =>
    (productsList || []).filter((p: Product) => p.actif).map((p: Product) => ({
      value: p.id,
      label: p.nomFr,
      sublabel: p.sku,
      imageUrl: p.imageUrl || undefined,
    })),
    [productsList]
  );

  const getProduct = (produitId: string) =>
    (productsList || []).find((p: Product) => p.id === produitId);

  const handleAddPiece = async (produitId: string) => {
    await createPiece.mutateAsync({ taskId, produitId });
    setAddingPiece(false);
  };

  const handleDeletePiece = async (pieceId: string) => {
    await deletePiece.mutateAsync({ id: pieceId, taskId });
  };

  return (
    <div className="border-t pt-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-muted-foreground" />
          <span className="font-semibold text-sm">Pièces en relation</span>
          <Badge variant="secondary" className="text-xs">{pieces.length}</Badge>
        </div>
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setAddingPiece(true)} data-testid="btn-add-piece">
          <Plus className="h-3 w-3 mr-1" />Ajouter une pièce
        </Button>
      </div>

      {addingPiece && (
        <div className="mb-3">
          <SearchSelect
            value={null}
            onValueChange={v => { if (v) handleAddPiece(v); }}
            options={productOptions}
            placeholder="Rechercher un produit..."
            emptyText="Aucun produit"
            data-testid="select-piece-product"
          />
          <Button variant="ghost" size="sm" className="mt-1 text-xs" onClick={() => setAddingPiece(false)}>Annuler</Button>
        </div>
      )}

      {isLoading ? (
        <p className="text-xs text-muted-foreground">Chargement...</p>
      ) : pieces.length === 0 ? (
        <p className="text-xs text-muted-foreground">Aucune pièce liée</p>
      ) : (
        <div className="space-y-2">
          {pieces.map((piece: TaskPiece) => {
            const product = getProduct(piece.produitId);
            return (
              <div key={piece.id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/30 border group" data-testid={`piece-${piece.id}`}>
                {product?.imageUrl && (
                  <img src={product.imageUrl} alt="" className="h-8 w-8 rounded object-cover shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{product?.nomFr || "Produit inconnu"}</p>
                  {product?.sku && <p className="text-xs text-muted-foreground">{product.sku}</p>}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => handleDeletePiece(piece.id)}
                  data-testid={`btn-delete-piece-${piece.id}`}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
